/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import MsDyn365 from '@msdyn365-commerce/core';
import { IArticle } from '../../actions/DataServiceEntities.g';
import ArticleCollectionCard from './components/articleCollectionCard';
import ArticleTagRefiner from './components/articleTagRefiner';
import classNames from 'classnames';
import { ILobasArticleCollectionData } from './lobas-article-collection.data';
import { ILobasArticleCollectionProps, ILobasArticleCollectionResources } from './lobas-article-collection.props.autogenerated';
import { UncontrolledPagination } from '@msdyn365-commerce-modules/utilities';
import { getCurrentUrl, hydrateRefinersFromUrl, parseQueryParam } from '../components/lobas-url-utils';
import { reaction, transaction } from 'mobx';
import { observer } from 'mobx-react';
import { ListPageState } from '../../actions/lobas-articles-list-page-state';
import getCollectionArticles, {
    GetFullArticlesByCollectionInput,
    IFullArticleResultsWithCount
} from '../../actions/get-all-articles.action';
import { SortColumn } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';

export interface ILobasArticleCollectionViewProps extends ILobasArticleCollectionProps<ILobasArticleCollectionData> {
    article: IArticle;
    resources: ILobasArticleCollectionResources;
    locale: string;
}

export interface ILobasArticleCollectionState {
    articles: IArticle[];
    currentPageNumber: number;
    totalArticleCount: number;
}
/**
 *
 * LobasArticleCollection component
 * @extends {React.PureComponent<ILobasArticleCollectionProps<ILobasArticleCollectionData>>}
 */
@observer
class LobasArticleCollection extends React.PureComponent<
    ILobasArticleCollectionProps<ILobasArticleCollectionData>,
    ILobasArticleCollectionState
> {
    public constructor(props: ILobasArticleCollectionProps<ILobasArticleCollectionData>, state: ILobasArticleCollectionState) {
        super(props);
        this.state = { articles: [], currentPageNumber: 0, totalArticleCount: 0 };
    }

    private readonly defaultPageNumber: number = 10;
    private isListPageStateUpdated: boolean = false;

    public async componentDidMount(): Promise<void> {
        const listPageState = await this.props.data.listPageState;
        const articles = await this.props.data.articles;

        this._updateListPageState(listPageState, articles);

        // Re-fetch the list in place whenever the selected tags or the current page change.
        reaction(
            () => {
                return [listPageState.activeTags.join(','), listPageState.currentPageNumber];
            },
            async () => {
                const newListPageState = await this.props.data.listPageState;
                const input = new GetFullArticlesByCollectionInput(
                    this.props.context.request.apiSettings,
                    {
                        Paging: {
                            Top: this.props.config.itemsPerPage || this.defaultPageNumber,
                            Skip: Math.max(newListPageState.pageSize * (newListPageState.currentPageNumber ?? 0), 0)
                        },
                        count: true,
                        Sorting: ArrayExtensions.hasElements(newListPageState.sortingCritera?.Columns)
                            ? newListPageState.sortingCritera ?? {}
                            : { Columns: [{ IsDescending: true, ColumnName: 'DateReleased' }] }
                    },
                    newListPageState.activeFilters ?? [],
                    this.props.context.request.locale
                );

                // Effective filter (AND) = authored base tags + the single selected tag, applied server-side.
                input.filterTags = this._getEffectiveFilterTags(newListPageState.activeTags);
                input.excludeUrl = this._getExcludeUrl();

                const articleResult = await getCollectionArticles(input, this.props.context.actionContext);
                transaction(() => {
                    newListPageState.totalTableCount = articleResult.count;
                    newListPageState.activeArticles = articleResult.articles;
                });
            }
        );
    }

    public render(): JSX.Element | null {
        const {
            data: { listPageState, articles }
        } = this.props;
        const rootClassName = classNames('lobasArticleCollection', this.props.config.className);

        if (!this.isListPageStateUpdated && listPageState.result && articles.result) {
            this._updateListPageState(listPageState.result, articles.result);
            this.isListPageStateUpdated = true;
        }

        // Render from the observable activeArticles so the list updates in place when tags/pages change.
        const articleList = listPageState.result?.activeArticles ?? [];
        const selectedTags = listPageState.result?.activeTags ?? [];

        return (
            <div className={rootClassName}>
                {this.props.config.showRefinerPanel && this._getRefinerPanel(selectedTags)}
                {articleList.map((a: IArticle) => {
                    const articleCollectionViewProps = {
                        article: a,
                        resources: this.props.resources,
                        locale: this.props.context.request.locale
                    } as ILobasArticleCollectionViewProps;
                    return (
                        <a
                            key={a.Id}
                            href={`${this.props.context.request.url.requestUrl.origin}${this.props.config.articlePageURL}/${a.URL}`}
                            target={this.props.config.openArticlesInNewTab ? '_blank' : 'self'}
                            rel='noreferrer'
                        >
                            <ArticleCollectionCard {...articleCollectionViewProps}></ArticleCollectionCard>
                        </a>
                    );
                })}
                {this.props.config.enablePagination && this._getPagination()}
            </div>
        );
    }

    /**
     * Renders the interactive tag refiner panel.
     * @param selectedTags - Currently selected tags.
     * @returns The panel, or null when there are no tags to show.
     */
    private readonly _getRefinerPanel = (selectedTags: string[]): React.ReactNode => {
        const tags = this.props.data.articleTags.result ?? [];
        if (!ArrayExtensions.hasElements(tags)) {
            return null;
        }
        return (
            <ArticleTagRefiner
                tags={tags}
                selectedTags={selectedTags}
                title={this.props.resources.refinerPanelTitle}
                clearAllLabel={this.props.resources.clearAllLabel}
                onSelect={this._onSelectTag}
                onClearAll={this._onClearAll}
            />
        );
    };

    /**
     * Selects a single tag (replacing any previous selection), or clears it when the active tag is clicked again.
     * Resets to page 1 and syncs the URL.
     * @param tag - The tag that was clicked.
     */
    private readonly _onSelectTag = (tag: string): void => {
        const listPageState = this.props.data.listPageState.result;
        if (!listPageState) {
            return;
        }
        const isActive = listPageState.activeTags.length === 1 && listPageState.activeTags[0] === tag;
        const updated = isActive ? [] : [tag];
        transaction(() => {
            listPageState.activeTags = updated;
            listPageState.currentPageNumber = 0;
        });
        this._syncTagsToUrl(updated);
    };

    /**
     * Clears all selected tags, resets to page 1, and syncs the URL.
     */
    private readonly _onClearAll = (): void => {
        const listPageState = this.props.data.listPageState.result;
        if (!listPageState) {
            return;
        }
        transaction(() => {
            listPageState.activeTags = [];
            listPageState.currentPageNumber = 0;
        });
        this._syncTagsToUrl([]);
    };

    /**
     * Reflects the selected tags in the URL (deep-linkable) without a full reload; the reaction does the fetch.
     * @param tags - The selected tags.
     */
    private readonly _syncTagsToUrl = (tags: string[]): void => {
        if (!MsDyn365.isBrowser) {
            return;
        }
        const url = new URL(window.location.href);
        if (ArrayExtensions.hasElements(tags)) {
            url.searchParams.set('tags', JSON.stringify(tags));
        } else {
            url.searchParams.delete('tags');
        }
        // Changing the filter always returns to page 1.
        url.searchParams.delete('skip');
        window.history.pushState({}, '', url.href);
    };

    /**
     * The effective tag filter (AND) = authored base tags + the single selected tag.
     * @param selectedTags - The currently selected tag(s) from the refiner panel (at most one).
     * @returns The effective filter tags, or undefined when none apply.
     */
    private readonly _getEffectiveFilterTags = (selectedTags: string[]): string | undefined => {
        const baseTags = this.props.config.filterByTags ?? [];
        const effectiveTags = [...baseTags, ...selectedTags];
        return effectiveTags.length > 0 ? effectiveTags.join(',') : undefined;
    };

    /**
     * The URL of the currently-viewed article to exclude, when the collection sits on an article detail page.
     * @returns The article URL to exclude, or undefined.
     */
    private readonly _getExcludeUrl = (): string | undefined => {
        const { articlePageURL } = this.props.config;
        if (!articlePageURL) {
            return undefined;
        }
        const urlParts = this.props.context.request.url.requestUrl.pathname.split('/');
        const pageName = urlParts[1];
        if (pageName === articlePageURL.replace('/', '')) {
            return urlParts[urlParts.length - 1];
        }
        return undefined;
    };

    private readonly _getPagination = (): React.ReactNode => {
        const { config, context, resources, data } = this.props;

        const listPageState = data.listPageState.result;
        const fullUrl = getCurrentUrl(context.request);
        const itemsPerPage = config.itemsPerPage || this.defaultPageNumber;
        // eslint-disable-next-line max-len -- existing code
        const skipCount = listPageState?.currentPageNumber
            ? listPageState.currentPageNumber * (this.props.config.itemsPerPage || this.defaultPageNumber)
            : (this.props.context.request.query && +this.props.context.request.query.skip) || 0;
        const totalItems = listPageState?.totalTableCount || 0;
        const previousText = resources.flipperPrevious;
        const nextText = resources.flipperNext;

        if (totalItems <= itemsPerPage) {
            return null;
        }
        return (
            <UncontrolledPagination
                className='ms-search-result-container__pagination'
                role='navigation'
                aria-label={resources.paginationAriaLabel}
                url={fullUrl.href}
                qsp='skip'
                items={totalItems}
                itemsPerPage={itemsPerPage}
                startingItem={skipCount}
                previousText={this._renderPrev(previousText)}
                nextText={this._renderNext(nextText)}
                previousAriaLabel={previousText}
                nextAriaLabel={nextText}
                //telemetryContent={this.telemetryContent}
            />
        );
    };

    /**
     * Renders a Pagination previous text.
     * @param  paginationPreviousText - Pagination previous text from resources.
     * @returns Previous text and flipper icon.
     */
    private readonly _renderPrev = (paginationPreviousText: string) => {
        return (
            <div className='msc-pagination__prev'>
                <span className='ms-search-result__pagination-left' aria-hidden='true' />
                <span className='prev-text'>{paginationPreviousText}</span>
            </div>
        );
    };

    /**
     * Renders a Pagination next text.
     * @param  paginationNextText - Pagination next text from resources.
     * @returns Next text and flipper icon.
     */
    private readonly _renderNext = (paginationNextText: string) => {
        return (
            <div className='msc-pagination__next'>
                <span className='next-text'>{paginationNextText}</span>
                <span className='ms-search-result__pagination-right' aria-hidden='true' />
            </div>
        );
    };

    /**
     * Update the list page state with initial data.
     * @param listPageState -- List page State.
     * @param articles -- Articles.
     */
    private readonly _updateListPageState = (listPageState: ListPageState, articles: IFullArticleResultsWithCount): void => {
        let querySorting: SortColumn[] = [];
        const defaultPageNumber: number = 10;

        if (this.props.context.request.query?.sorting) {
            querySorting = JSON.parse(decodeURIComponent(this.props.context.request.query.sorting)) as SortColumn[];
        }

        listPageState.currentPageNumber =
            (this.props.context.request.query &&
                +this.props.context.request.query.skip / (this.props.config.itemsPerPage ?? defaultPageNumber)) ||
            0;
        listPageState.sortingCritera = { Columns: querySorting };
        listPageState.pageSize = this.props.config.itemsPerPage ?? defaultPageNumber;
        listPageState.activeArticles = articles.articles;
        listPageState.totalTableCount = articles.count;
        listPageState.activeFilters = hydrateRefinersFromUrl(this.props.context.request);
        // Hydrate the selected tags from the URL so refresh / deep-links restore the filtered view.
        listPageState.activeTags = parseQueryParam<string[]>('tags', this.props.context.request) ?? [];
    };
}

export default LobasArticleCollection;
